// Multiple-choice questions about the host, shown while a guest waits to
// be let in (Adam, 2026-10-13: "as part of the morning routine i want you to
// make me give you 3 facts about myself ... i envision this being on waiting
// for the host to let you in screen, and it being 3 question MC that they can
// click through"). The questions come from the host's Seamless, the whole
// bank in a random order (Adam, 2026-10-14: "they should be able to go through
// all the questions in the bank, not just 3 at a time"); with none, or
// when the request fails, nothing shows. Tap a choice to see whether it was
// right, then go on; after the last the card shows the score and Play again,
// which deals a fresh set with the choices in a new order (Adam, 2026-10-14:
// "add a replay button after getting through all trivia"). The X closes it.
// Every load deals the questions in a new order and starts at the first with
// nothing remembered (Adam, 2026-10-15: "if someone completes all trivia and
// plays again the questions should show up in random order", and "whenever i
// enter the trivia anew it should start as if i had never played it"); the
// endpoint is cached for 15 s, so its own order cannot be trusted for that.
// The exception is the questions he placed in his editor (QUEUE row 429:
// "Have a surface on my own meet.am where I can edit and reorder trivia"):
// the first round deals those first, in his order, and the rest after them
// mixed; Play again still mixes everything. The editor opens from a pencil on
// this card in his own waiting room preview (row 431). Anyone can drag the
// card by its top line or shrink it to that line (row 432: "make the trivia
// card draggable around the screen or minimizable in addition to xable").
// A guest who comes back, even months later, gets the questions this device
// has not answered first (Seamless QUEUE row 1844: "see the questions they
// have not yet completed first"); his own preview remembers nothing.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX, type PointerEvent as ReactPointerEvent } from "react";
import { ROOT_HANDLE } from "@breeze/core";
import { ChevronIcon, CloseIcon, MinusIcon, PencilIcon } from "./icons";
import { TriviaEditor, type BankQuestion } from "./TriviaEditor";

/** The trivia is about the operator, so only their room shows it (and zz- test
 * rooms, which no real guest reaches). Another owner's guests never see it. */
export function showsTrivia(kind: string, slug: string): boolean {
  return (
    kind === "personal" &&
    (slug.toLowerCase() === ROOT_HANDLE || slug.startsWith("zz-"))
  );
}

/** The whole bank: Seamless clamps n to what it allows. */
const MAX_QUESTIONS = 500;
const TRIVIA_URL = `https://seamless.adammirmina.com/api/trivia?n=${MAX_QUESTIONS}`;
const FETCH_TIMEOUT_MS = 6000;

export interface TriviaQuestion {
  readonly id: string;
  readonly question: string;
  readonly choices: readonly string[];
  readonly answer: number;
  /** He placed it in his editor, so the first round keeps it in his order. */
  readonly placed?: boolean;
}

/** Keeps only well-formed questions; anything else in the response is dropped. */
export function parseTrivia(raw: unknown): TriviaQuestion[] {
  if (!Array.isArray(raw)) return [];
  const out: TriviaQuestion[] = [];
  for (const q of raw) {
    if (typeof q !== "object" || q === null) continue;
    const { id, question, choices, answer, placed } = q as Record<string, unknown>;
    if (typeof question !== "string" || !question.trim()) continue;
    if (
      !Array.isArray(choices) ||
      choices.length < 2 ||
      !choices.every((c) => typeof c === "string" && c.trim())
    )
      continue;
    if (
      typeof answer !== "number" ||
      !Number.isInteger(answer) ||
      answer < 0 ||
      answer >= choices.length
    )
      continue;
    out.push({
      id: typeof id === "string" ? id : String(out.length),
      question,
      choices: choices as string[],
      answer,
      ...(placed === true ? { placed: true } : {}),
    });
    if (out.length === MAX_QUESTIONS) break;
  }
  return out;
}

/** A copy of `items` in a random order (Fisher-Yates). */
export function shuffled<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** The first round: his placed questions in the order they came, then the rest mixed. */
export function firstDeal(items: readonly TriviaQuestion[]): TriviaQuestion[] {
  return [...items.filter((q) => q.placed), ...shuffled(items.filter((q) => !q.placed))];
}

/** Where a guest's device keeps the ids it has answered, with no expiry. */
const SEEN_KEY = "breeze.trivia.seen";
/** Enough for years of questions; the oldest answers drop off past it. */
const MAX_SEEN = 2000;

/** The ids this device has answered; empty when storage is unavailable. */
export function readSeen(): Set<string> {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "[]");
    return new Set(Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

/** Remembers that this device answered `id`. */
export function markSeen(id: string): void {
  try {
    const ids = [...readSeen()].filter((x) => x !== id);
    ids.push(id);
    localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(-MAX_SEEN)));
  } catch {
    // Private mode or full storage: the next visit deals as if new.
  }
}

/** The first round for a returning guest: what they have not answered (his placed ones first), then the rest mixed. */
export function dealUnseen(items: readonly TriviaQuestion[], seen: ReadonlySet<string>): TriviaQuestion[] {
  return [...firstDeal(items.filter((q) => !seen.has(q.id))), ...shuffled(items.filter((q) => seen.has(q.id)))];
}

/** More choices than this is a range (every height from 5'5" to 6'2"), which reads in its own order. */
const RANGE = 4;

/** The same question with its choices in a new order, so a replay is not answered by position. A range keeps its order. */
export function reshuffle(q: TriviaQuestion): TriviaQuestion {
  if (q.choices.length > RANGE) return q;
  const order = shuffled(q.choices.map((_, i) => i));
  return {
    ...q,
    choices: order.map((i) => q.choices[i]!),
    answer: order.indexOf(q.answer),
  };
}

/** What the card tells the host's log (QUEUE row 416): each question shown,
 * each answer, the score at the end, and a close. */
export interface TriviaEvent {
  readonly event: "shown" | "answered" | "finished" | "closed";
  readonly q?: string;
  readonly pick?: string;
  readonly right?: boolean;
  readonly score?: number;
  readonly of?: number;
}

/** How far a press on the top line travels before it is a drag rather than a tap. */
const DRAG_SLOP = 5;
/** The gap a dragged card keeps from the edges of the screen. */
const EDGE = 8;
// Room kept free at the bottom for the mic, camera, and hang up buttons, so a
// dragged card never covers Leave.
const CONTROLS = 80;
// Below this width the card spans the screen, so a drag moves it up and down
// only (QUEUE row 447); the stylesheet's phone layout uses the same width.
const PHONE_QUERY = "(max-width: 767px)";
const isPhone = (): boolean => typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches;

export function WaitingTrivia({
  onEvent,
  editable = false,
}: { readonly onEvent?: (event: TriviaEvent) => void; readonly editable?: boolean } = {}): JSX.Element | null {
  const [questions, setQuestions] = useState<TriviaQuestion[]>([]);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [closed, setClosed] = useState(false);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [editing, setEditing] = useState(false);
  // Where he dragged it, from the top left of the screen; null keeps the stylesheet's place.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const card = useRef<HTMLElement>(null);
  const bank = useRef<BankQuestion[] | null>(null);
  const press = useRef<{ x: number; y: number; left: number; top: number; moved: boolean } | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
    fetch(TRIVIA_URL, { signal: ctrl.signal, cache: "no-store" })
      .then((r) => (r.ok ? r.json() : []))
      .then((raw: unknown) => {
        const all = parseTrivia(raw);
        const next = (round === 0 ? (editable ? firstDeal(all) : dealUnseen(all, readSeen())) : shuffled(all)).map(reshuffle);
        // A replay that fails to load deals the last set again rather than closing the card.
        setQuestions((prev) =>
          next.length ? next : round > 0 ? shuffled(prev).map(reshuffle) : [],
        );
        setIndex(0);
        setPicked(null);
        setScore(0);
        setFinished(false);
      })
      .catch(() => {
        if (round === 0) setQuestions([]);
      })
      .finally(() => {
        clearTimeout(timer);
        if (!ctrl.signal.aborted || round === 0) setLoaded(true);
      });
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [round]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keeps a dragged card on the screen when the window or the card changes size.
  const clamp = useCallback((want: { left: number; top: number }) => {
    const el = card.current;
    const parent = el?.offsetParent as HTMLElement | null | undefined;
    if (!el || !parent) return want;
    const maxLeft = Math.max(EDGE, parent.clientWidth - el.offsetWidth - EDGE);
    const maxTop = Math.max(EDGE, parent.clientHeight - el.offsetHeight - CONTROLS);
    return { left: Math.min(Math.max(EDGE, want.left), maxLeft), top: Math.min(Math.max(EDGE, want.top), maxTop) };
  }, []);
  useLayoutEffect(() => {
    if (!pos) return;
    const fit = (): void =>
      setPos((p) => {
        if (!p) return p;
        const c = clamp(p);
        return c.left === p.left && c.top === p.top ? p : c;
      });
    const el = card.current;
    const ro = typeof ResizeObserver === "undefined" || !el ? null : new ResizeObserver(fit);
    if (el) ro?.observe(el);
    window.addEventListener("resize", fit);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [pos !== null, clamp]); // eslint-disable-line react-hooks/exhaustive-deps

  // The whole card is the handle, not just its top line: he grabs it by the
  // question (QUEUE row 436). Buttons, fields, and the editor's list keep
  // their own presses.
  const onHeadDown = (e: ReactPointerEvent<HTMLElement>): void => {
    if (e.button !== 0 || !card.current) return;
    if ((e.target as HTMLElement).closest('button, input, textarea, select, a, label, [role="radio"], .tq-list, .tq-form')) return;
    const el = card.current;
    const parent = el.offsetParent as HTMLElement | null;
    if (!parent) return;
    const r = el.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();
    press.current = { x: e.clientX, y: e.clientY, left: r.left - pr.left, top: r.top - pr.top, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onHeadMove = (e: ReactPointerEvent<HTMLElement>): void => {
    const p = press.current;
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    if (!p.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    if (!p.moved) {
      p.moved = true;
      setDragging(true);
    }
    setPos(clamp({ left: isPhone() ? p.left : p.left + dx, top: p.top + dy }));
  };
  const onHeadUp = (): void => {
    const p = press.current;
    press.current = null;
    if (!p) return;
    setDragging(false);
    // A tap on the top line of a shrunk card opens it again.
    if (!p.moved && minimized) setMinimized(false);
  };

  const q = questions[index];
  // Each question once, as it comes up.
  const told = useRef<TriviaQuestion | null>(null);
  const report = useRef(onEvent);
  report.current = onEvent;
  useEffect(() => {
    if (closed || finished || minimized || editing || !q || told.current === q) return;
    told.current = q;
    report.current?.({ event: "shown", q: q.question });
  }, [q, closed, finished, minimized, editing]);
  if (closed) return null;
  // The owner keeps the card with an empty bank, so the pencil can add the first question.
  if (!q && !(editable && loaded)) return null;
  const last = index === questions.length - 1;
  const answered = picked !== null;

  const closeEditor = (): void => {
    setEditing(false);
    // His edits show at once: the public list is cached for 15 s, so the card
    // deals from the bank the editor last saw instead of fetching it again.
    if (bank.current) {
      setQuestions(firstDeal(parseTrivia(bank.current)).map(reshuffle));
      setIndex(0);
      setPicked(null);
      setScore(0);
      setFinished(false);
    }
  };

  const label = editing
    ? "Questions"
    : finished || !q
      ? "While you wait"
      : `${index + 1} of ${questions.length}`;

  return (
    <section
      ref={card}
      className={`trivia${minimized ? " trivia--min" : ""}${editing ? " trivia--editing" : ""}${dragging ? " trivia--dragging" : ""}${pos ? " trivia--placed" : ""}`}
      style={pos ? { left: pos.left, top: pos.top } : undefined}
      aria-label="While you wait"
      data-testid="waiting-trivia"
      onPointerDown={onHeadDown}
      onPointerMove={onHeadMove}
      onPointerUp={onHeadUp}
      onPointerCancel={onHeadUp}
    >
      <div className="trivia__head" data-testid="trivia-handle">
        <span className="trivia__count">{minimized && !editing ? `Trivia, ${label.toLowerCase()}` : label}</span>
        <span className="trivia__tools">
          {editable ? (
            <button
              type="button"
              className={`trivia__x${editing ? " is-on" : ""}`}
              aria-label={editing ? "Done editing" : "Edit the questions"}
              aria-pressed={editing}
              title={editing ? "Done editing" : "Edit the questions"}
              data-testid="trivia-edit"
              onClick={() => {
                if (editing) closeEditor();
                else {
                  setMinimized(false);
                  setEditing(true);
                }
              }}
            >
              <PencilIcon size={17} />
            </button>
          ) : null}
          <button
            type="button"
            className="trivia__x"
            aria-label={minimized ? "Open" : "Minimize"}
            aria-expanded={!minimized}
            title={minimized ? "Open" : "Minimize"}
            data-testid="trivia-min"
            onClick={() => setMinimized((m) => !m)}
          >
            {minimized ? (
              <span className="trivia__open" aria-hidden="true">
                <ChevronIcon size={18} />
              </span>
            ) : (
              <MinusIcon size={18} />
            )}
          </button>
          <button
            type="button"
            className="trivia__x"
            aria-label="Close"
            title="Close"
            onClick={() => {
              setClosed(true);
              onEvent?.({ event: "closed", ...(finished || !q ? {} : { q: q.question }), score, of: questions.length });
            }}
          >
            <CloseIcon size={18} />
          </button>
        </span>
      </div>
      {minimized ? null : editing ? (
        <TriviaEditor onBank={(b) => (bank.current = b)} />
      ) : !q ? (
        <p className="trivia__q">No questions yet.</p>
      ) : finished ? (
        <>
          <p className="trivia__q">
            You got {score} of {questions.length}.
          </p>
          <div className="trivia__foot">
            <button
              type="button"
              className="trivia__next"
              onClick={() => setRound((r) => r + 1)}
            >
              Play again
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="trivia__q">{q.question}</p>
          <div className={`trivia__choices${q.choices.length > RANGE ? " trivia__choices--grid" : ""}`} role="group" aria-label="Choices">
            {q.choices.map((choice, i) => {
              const state = !answered
                ? ""
                : i === q.answer
                  ? " trivia__choice--right"
                  : i === picked
                    ? " trivia__choice--wrong"
                    : "";
              return (
                <button
                  key={i}
                  type="button"
                  className={`trivia__choice${state}`}
                  disabled={answered}
                  aria-pressed={picked === i}
                  onClick={() => {
                    setPicked(i);
                    if (i === q.answer) setScore((n) => n + 1);
                    if (!editable) markSeen(q.id);
                    onEvent?.({ event: "answered", q: q.question, pick: choice, right: i === q.answer });
                  }}
                >
                  {choice}
                </button>
              );
            })}
          </div>
          {answered ? (
            <div className="trivia__foot">
              <span className="trivia__result" role="status">
                {picked === q.answer ? "Right." : "Not quite."}
              </span>
              <button
                type="button"
                className="trivia__next"
                onClick={() => {
                  if (last) {
                    setFinished(true);
                    onEvent?.({ event: "finished", score, of: questions.length });
                  } else {
                    setIndex(index + 1);
                    setPicked(null);
                  }
                }}
              >
                {last ? "Done" : "Next"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
